<template>
    <div class="chat-header">
        <ChatCloseSettingsButton
            :contact="contact"
            :autoTranslateEnabled="autoTranslateEnabled"
            :debugModeEnabled="debugModeEnabled"
            @autoTranslateChanged="autoTranslateChanged"
            @debugModeChanged="debugModeChanged"
            :is-watching="isWatching"
        />
        <TranslateDetected :contact="contact" @auto-translate="autoTranslateChanged" />
    </div>
</template>
<script>
import { mapMutations, mapState } from "vuex";
import { log2 } from "../../../utils/log2";
import { capitalize } from "../../../utils/utils";
import { sendSocketMessage } from "../../../utils/socketStream";
import ChatCloseSettingsButton from "./ChatCloseSettingsButton.vue";
import TranslateDetected from "../TranslateDetected.vue";

const log = log2("ChatHeader");
export default {
    name: "ChatHeader",
    components: {
        TranslateDetected,
        ChatCloseSettingsButton,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        isWatching: {
            type: Boolean,
            default: false,
        },
        messages: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            authUser: (state) => state.auth.user,
            siteId: (state) => state.site.site._id,
        }),
    },
    data: () => ({
        autoTranslateEnabled: false,
        debugModeEnabled: false,
        translationBatchTimers: [],
    }),
    mounted: function () {
        this.autoTranslateEnabled = this.autoTranslate.includes(this.contact.lastConversation._id);
    },
    beforeDestroy() {
        this.cancelExistingTranslationQueue();
    },
    methods: {
        ...mapMutations({
            setAutoTranslate: "chat/SET_AUTO_TRANSLATE",
        }),
        capitalize,
        autoTranslateChanged(val) {
            const fn = () => {
                const wasEnabled = this.autoTranslateEnabled;
                this.autoTranslateEnabled = val;
                this.setAutoTranslate({
                    id: this.contact.lastConversation._id,
                    state: val,
                });
                if (val) {
                    this.$message.success({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.on")}`,
                        duration: 3,
                        key: "translate",
                    });
                    if (!wasEnabled) {
                        this.confirmTranslateExistingMessages();
                    }
                } else {
                    this.$message.warning({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.off")}`,
                        duration: 3,
                        key: "translate",
                    });
                }
            };
            this.$feature.beforeAutoTranslation(fn);
        },
        confirmTranslateExistingMessages() {
            const baseLang = this.contact && this.contact.preferredLang;
            if (!baseLang || baseLang === this.authUser.lang) return;
            if (!this.messages || !this.messages.length) return;

            this.$confirm({
                title: this.$t("message.translateExistingMessagesTitle"),
                content: this.$t("message.translateExistingMessagesDesc"),
                okText: this.$t("message.yes"),
                cancelText: this.$t("message.no"),
                onOk: () => this.translateExistingMessages(),
            });
        },
        translateExistingMessages() {
            const baseLang = this.contact && this.contact.preferredLang;
            const userLang = this.authUser.lang;
            if (!baseLang || baseLang === userLang) return;
            const conversationId = this.contact && this.contact.lastConversation && this.contact.lastConversation._id;

            // Backend'i ve socket kuyruğunu korumak için sınırlar:
            // - En fazla son MAX_MESSAGES_TO_TRANSLATE mesaj çevrilir (eski mesajlara dokunulmaz)
            // - BATCH_SIZE'lık gruplar halinde, BATCH_DELAY_MS aralıklarla yollanır
            const MAX_MESSAGES_TO_TRANSLATE = 100;
            const BATCH_SIZE = 10;
            const BATCH_DELAY_MS = 250;

            const list = this.messages || [];
            const pending = [];
            // En yeniden eskiye doğru tarayıp görünür mesajlara öncelik veriyoruz
            for (let i = list.length - 1; i >= 0 && pending.length < MAX_MESSAGES_TO_TRANSLATE; i--) {
                const message = list[i];
                if (!message || !message.raw) continue;
                if (message.type !== "text") continue;
                const translates = message.raw.translates || [];
                const alreadyTranslated = translates.find(
                    (t) => t.to === userLang && t.from === baseLang
                );
                if (alreadyTranslated) continue;
                pending.push(message);
            }

            if (pending.length === 0) {
                this.$message.info({
                    content: this.$t("message.translateExistingMessagesNothing"),
                    duration: 3,
                    key: "translate-existing",
                });
                return;
            }

            this.$message.loading({
                content: this.$t("message.translateExistingMessagesInProgress", { count: pending.length }),
                duration: 3,
                key: "translate-existing",
            });

            this.cancelExistingTranslationQueue();
            for (let i = 0; i < pending.length; i += BATCH_SIZE) {
                const batch = pending.slice(i, i + BATCH_SIZE);
                const batchIndex = i / BATCH_SIZE;
                const timer = setTimeout(() => {
                    batch.forEach((message) => {
                        sendSocketMessage("user translate-message", {
                            siteId: this.siteId,
                            to: userLang,
                            data: Object.assign({}, message.raw, {
                                baseLang,
                                message: message.raw.description || message.raw.message,
                                conversationId:
                                    message.raw.conversationId || message.raw.conversation || conversationId,
                            }),
                        });
                    });
                }, batchIndex * BATCH_DELAY_MS);
                this.translationBatchTimers.push(timer);
            }
        },
        cancelExistingTranslationQueue() {
            if (this.translationBatchTimers && this.translationBatchTimers.length) {
                this.translationBatchTimers.forEach((t) => clearTimeout(t));
                this.translationBatchTimers = [];
            }
        },
        debugModeChanged(val) {
            this.debugModeEnabled = val;
            this.$emit('debugModeChanged', val);
            if (val) {
                this.$message.success({
                    content: "Chatbot Debug Mode Açık",
                    duration: 3,
                    key: "debug",
                });
            } else {
                this.$message.info({
                    content: "Chatbot Debug Mode Kapalı",
                    duration: 3,
                    key: "debug",
                });
            }
        },
    },
    watch: {
        autoTranslate: {
            deep: true,
            handler() {
                this.autoTranslateEnabled = this.autoTranslate.includes(this.contact.lastConversation._id);
            },
        },
    },
};
</script>
